import type {InfiniteData} from '@tanstack/react-query';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {applyMyFeedback, signalKeys, submitFeedback} from './queries';
import type {
  FeedbackErrorReason,
  FeedbackSubmission,
  IndicatorsByMemberPage,
  MyFeedback,
} from './types';

class SignalGoneError extends Error {
  constructor() {
    super('Signal is no longer available');
    this.name = 'SignalGoneError';
  }
}

export function useSignalFeedback() {
  const queryClient = useQueryClient();
  const [feedbackSignalId, setFeedbackSignalId] = useState<string | null>(null);

  // Append-only submit: optimistically writes myFeedback, rolls back on failure.
  const submitFeedbackMutation = useMutation({
    mutationFn: async (vars: {
      indicatorId: string;
      submission: FeedbackSubmission;
    }) => {
      const result = await submitFeedback({
        indicatorId: vars.indicatorId,
        formId: vars.submission.formId,
        answers: vars.submission.answers,
      });
      if (result.status === 'gone') throw new SignalGoneError();
      return result.myFeedback;
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({queryKey: signalKeys.byMemberAll});
      const previous = queryClient.getQueriesData<
        InfiniteData<IndicatorsByMemberPage>
      >({queryKey: signalKeys.byMemberAll});
      const optimistic: MyFeedback = {
        id: 'optimistic',
        formId: vars.submission.formId,
        submittedAt: new Date().toISOString(),
        // Read answers require value; the server echo replaces this on success
        answers: vars.submission.answers.map((answer) => ({
          ...answer,
          value: answer.value ?? '',
        })),
      };
      queryClient.setQueriesData<InfiniteData<IndicatorsByMemberPage>>(
        {queryKey: signalKeys.byMemberAll},
        (cached) => applyMyFeedback(cached, vars.indicatorId, optimistic),
      );
      return {previous};
    },
    onError: (_error, _vars, context) => {
      for (const [key, cached] of context?.previous ?? []) {
        queryClient.setQueryData(key, cached);
      }
    },
    onSuccess: (myFeedback, vars) => {
      queryClient.setQueriesData<InfiniteData<IndicatorsByMemberPage>>(
        {queryKey: signalKeys.byMemberAll},
        (cached) => applyMyFeedback(cached, vars.indicatorId, myFeedback),
      );
      setFeedbackSignalId(null);
    },
  });

  // Reset any stale pending/error before (re)opening the form for a signal.
  const openFeedback = (signalId: string) => {
    submitFeedbackMutation.reset();
    setFeedbackSignalId(signalId);
  };

  const closeFeedback = () => setFeedbackSignalId(null);

  const closeFeedbackFor = (signalId: string) =>
    setFeedbackSignalId((current) => (current === signalId ? null : current));

  const feedbackError = feedbackErrorReason(submitFeedbackMutation.error);

  return {
    feedbackSignalId,
    openFeedback,
    closeFeedback,
    closeFeedbackFor,
    submitFeedback: submitFeedbackMutation,
    feedbackError,
  };
}

function feedbackErrorReason(
  error: Error | null,
): FeedbackErrorReason | undefined {
  if (!error) return undefined;
  return error instanceof SignalGoneError ? 'gone' : 'generic';
}
